/**
 * DetailedItinerary Component
 * 
 * Displays full day-wise itinerary with descriptions and activities.
 * Uses normalized data from QuotationFormatter.getDetailedItinerary()
 * 
 * Usage:
 *   const formatter = new QuotationFormatter(quotation, agency);
 *   const detailedData = formatter.getDetailedItinerary();
 *   
 *   <DetailedItinerary
 *     data={detailedData}
 *     packageType={quotation.route_type}
 *     variant="detail"
 *   />
 */

import React from 'react';
import { FileText, MapPin, Utensils, Bed } from 'lucide-react';
import { COMPONENT_VARIANTS, formatItineraryTitle } from './displayConfig';

const DetailedItinerary = ({ 
  data = [],
  packageType = 'custom',
  variant = 'detail',
  showTitle = true,
  editable = false,
  onEdit = null,
  className = ''
}) => {
  if (!data || data.length === 0) {
    return null;
  }

  const styles = COMPONENT_VARIANTS[variant] || COMPONENT_VARIANTS.detail;

  return (
    <div 
      className={className}
      data-package-type={packageType}
      data-component="detailed-itinerary"
    >
      {showTitle && (
        <h3 className={`font-semibold ${variant === 'preview' ? 'text-sm' : 'text-lg'} mb-3 flex items-center gap-2`}>
          <FileText className={`${variant === 'preview' ? 'w-4 h-4' : 'w-5 h-5'} text-[var(--navy)]`} />
          Day-wise Itinerary
          {editable && onEdit && (
            <button 
              onClick={onEdit}
              className="ml-auto text-xs text-[var(--navy)] hover:underline"
            >
              Edit
            </button>
          )}
        </h3>
      )}
      
      <div className="space-y-4">
        {data.map((day, idx) => (
          <div 
            key={idx}
            className="border border-gray-200 rounded-lg overflow-hidden"
            data-day={day.day}
          >
            {/* Day Header */}
            <div className={`${styles.headerBg} ${styles.headerText} px-4 py-2 flex items-center justify-between`}>
              <span className="font-semibold">
                Day {day.day}: {formatItineraryTitle(day.title)}
              </span>
              {day.date && (
                <span className={`${variant === 'preview' ? 'text-[10px]' : 'text-xs'} opacity-80`}>
                  {day.date}
                </span>
              )}
            </div>
            
            {/* Day Content */}
            <div className="p-4 space-y-3">
              {/* Description */}
              {day.description && (
                <p className={`${styles.fontSize} text-gray-700 leading-relaxed`}>
                  {day.description}
                </p>
              )}
              
              {/* Highlights */}
              {day.highlights && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[var(--navy)] mt-0.5 shrink-0" />
                  <div>
                    <span className={`${styles.fontSize} font-medium text-[var(--navy)]`}>Highlights: </span>
                    <span className={`${styles.fontSize} text-gray-600`}>{day.highlights}</span>
                  </div>
                </div>
              )}
              
              {/* Meals */}
              {day.meals && (
                <div className="flex items-start gap-2">
                  <Utensils className="w-4 h-4 text-[var(--cyan)] mt-0.5 shrink-0" />
                  <div>
                    <span className={`${styles.fontSize} font-medium text-[var(--navy)]`}>Meals: </span>
                    <span className={`${styles.fontSize} text-gray-600`}>{day.meals}</span>
                  </div>
                </div>
              )}
              
              {/* Optional Activities */}
              {day.optionalActivities && (
                <div className="mt-2 p-2 bg-[color-mix(in_srgb,var(--navy)_5%,white)] rounded border border-[color-mix(in_srgb,var(--navy)_15%,transparent)]">
                  <span className={`${styles.fontSize} font-medium text-[var(--navy)]`}>Optional Activities: </span>
                  <span className={`${styles.fontSize} text-gray-700`}>{day.optionalActivities}</span>
                </div>
              )}
              
              {/* Overnight Stay */}
              {day.overnightAt && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                  <Bed className="w-4 h-4 text-[var(--navy)]" />
                  <span className={`${styles.fontSize} text-[var(--navy)]`}>
                    Overnight: <strong>{day.overnightAt}</strong>
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DetailedItinerary;
